import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Line } from 'recharts';

interface JuridicaIncomeData {
  chart_data: any[];
  income_table: any[];
  gains_table: any[];
}

interface JuridicaIncomeViewProps {
  data: JuridicaIncomeData;
  title: string;
}

const formatYAxis = (tickItem: number) => {
  if (tickItem >= 1000000000) {
    return `${(tickItem / 1000000000).toFixed(1)}B`;
  }
  if (tickItem >= 1000000) {
    return `${(tickItem / 1000000).toFixed(1)}M`;
  }
  if (tickItem >= 1000) {
    return `${(tickItem / 1000).toFixed(1)}K`;
  }
  return tickItem.toString();
};

const JuridicaIncomeView: React.FC<JuridicaIncomeViewProps> = ({ data, title }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (!data || !data.chart_data || data.chart_data.length === 0) {
    return (
      <Paper sx={{ p: 2, textAlign: 'center' }}>
        <Typography>No hay datos disponibles para {title}.</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" gutterBottom>Análisis de Ingresos y Ganancias</Typography>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="sub-vistas de ingresos y ganancias">
          <Tab label="Gráfica" />
          <Tab label="Tabla de Ingresos" />
          <Tab label="Tabla de Ganancias" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6">Total de Ingresos y Ganancias con Tendencia</Typography>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data.chart_data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="Anio" />
              <YAxis tickFormatter={formatYAxis} />
              <Tooltip formatter={(value: number) => new Intl.NumberFormat('es-CO').format(value)} />
              <Legend />
              <Bar dataKey="Total_Ingresos_Ganancias" fill={theme.palette.primary.main} name="Total Ingresos Ganancias" />
              <Line type="monotone" dataKey="Total_Ingresos_Ganancias" stroke={theme.palette.secondary.main} name="Tendencia" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {activeTab === 1 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6">Tabla de Ingresos</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Año</TableCell>
                  <TableCell>Total Abonos</TableCell>
                  <TableCell>Ingresos por Ganancias Ocasionales</TableCell>
                  <TableCell>Devoluciones, Rebajas y Descuentos en Ventas</TableCell>
                  <TableCell>Total Ingresos Netos</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.income_table.map((row: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{row.Anio}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Total_Abonos)}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Ingresos_por_ganancias_ocasionales)}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Devoluciones_rebajas_descuentos_ventas)}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Total_ingresos_netos)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {activeTab === 2 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6">Tabla de Ganancias</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Año</TableCell>
                  <TableCell>Total Ingresos Ganancias</TableCell>
                  <TableCell>Análisis %</TableCell>
                  <TableCell>Análisis $</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.gains_table.map((row: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{row.Anio}</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row.Total_Ingresos_Ganancias)}</TableCell>
                    <TableCell>{row['Analisis %'] ? row['Analisis %'].toFixed(2) : '0.00'}%</TableCell>
                    <TableCell>{new Intl.NumberFormat('es-CO').format(row['Analisis $'])}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default JuridicaIncomeView;